import { berlinZeitpunkt } from "@dj/domain/gemeinsam";
import { useState } from "react";
import { Abweisung } from "../design/Hinweis";
import { Kachel } from "../design/Kachel";
import { Symbol } from "../design/Symbol";
import { abweisungsgrund, befehl } from "../plattform/api";
import { useSammlung, type Zeile } from "../plattform/lesemodelle";
import { useSitzung } from "../plattform/sitzung";

/**
 * Einladungen in Jagdherrenkanäle (oberflaeche.md §7 „Kanäle“, V-18; lesemodelle.md §6; Anforderungen §7a K88 b):
 * `region` „Einladungen“ über den Kanälen, je Kanal und eingeladenem eigenen Revier ein `article` mit dem einladenden
 * Revier, dem Zeitpunkt und dem Status; solange das eigene Revier eingeladen ist, „Annehmen“ und „Ablehnen“. Ist die
 * Person Jagdherr mehrerer Reviere, die in denselben Kanal eingeladen sind, nennt jeder Eintrag zusätzlich das eigene
 * Revier (Name aus „Meine Reviere“), damit klar ist, für welches Revier sie antwortet (N2-L1). Ohne Einladung steht
 * nichts da.
 */
type Antwort = "JagdherrenkanalEinladungAnnehmen" | "JagdherrenkanalEinladungAblehnen";

export function Einladungen() {
  const { uid } = useSitzung();
  const einladungen = useSammlung(uid ? `personen/${uid}/jagdherrenkanal_einladungen` : null);
  const reviere = useSammlung(uid ? `personen/${uid}/reviere` : null);
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [grund, setGrund] = useState<string | null>(null);
  if (einladungen.daten.length === 0) return null;
  const kennung = (z: Zeile, k: "Kanal" | "Revier") => (z["_kennungen"] as Record<string, string> | undefined)?.[k] ?? null;
  const liste = [...einladungen.daten].sort((a, b) => zeit(b) - zeit(a));
  const jeKanal = new Map<string | null, number>();
  for (const z of liste) jeKanal.set(kennung(z, "Kanal"), (jeKanal.get(kennung(z, "Kanal")) ?? 0) + 1);
  const eigenesRevier = (z: Zeile): string | null => {
    if ((jeKanal.get(kennung(z, "Kanal")) ?? 0) < 2) return null;
    const r = reviere.daten.find((x) => x._id === kennung(z, "Revier"));
    return r ? `für ${String(r["Revier"])}` : null;
  };

  async function antworten(z: Zeile, name: Antwort) {
    setGrund(null);
    setLaeuft(z._id);
    const a = await befehl(name, { Kanal: kennung(z, "Kanal"), Revier: kennung(z, "Revier") });
    setLaeuft(null);
    if (!a.ok) setGrund(abweisungsgrund(a));
  }

  return (
    <section className="liste-bereich" role="region" aria-label="Einladungen">
      <h2 className="kopf"><Symbol name="einladung" />Einladungen</h2>
      {liste.map((z) => {
        const titel = `einladung-${z._id}`;
        const offen = z["Status"] === "eingeladen" && laeuft !== z._id;
        const am = z["eingeladen am"] instanceof Date ? `eingeladen am ${berlinZeitpunkt(z["eingeladen am"] as Date)}` : null;
        return (
          <article key={z._id} className="eintrag eintrag--knoepfe platte" aria-labelledby={titel}>
            <h3 className="eintrag__t" id={titel}>{String(z["Kanal"] ?? "Jagdherrenkanal")}</h3>
            <span className="eintrag__meta">{[eigenesRevier(z), z["einladendes Revier"], am, z["Status"]].filter(Boolean).join(" · ")}</span>
            {offen ? (
              <div className="raster raster--knoepfe">
                <Kachel symbol="haken" begriff="Annehmen" art="kompakt" onPress={() => void antworten(z, "JagdherrenkanalEinladungAnnehmen")} />
                <Kachel symbol="schliessen" begriff="Ablehnen" art="kompakt" onPress={() => void antworten(z, "JagdherrenkanalEinladungAblehnen")} />
              </div>
            ) : null}
          </article>
        );
      })}
      <Abweisung grund={grund} />
    </section>
  );
}

function zeit(z: Zeile): number {
  return z["eingeladen am"] instanceof Date ? (z["eingeladen am"] as Date).getTime() : 0;
}
